import type { AppSearchAction } from '../types';

export const PATH_SEPARATOR = ' / ';

export type ParsedSearchQuery = {
  scope: string[];
  filter: string;
  entered: boolean;
};

export const parseSearchQuery = (query: string): ParsedSearchQuery => {
  const entered = query.endsWith(PATH_SEPARATOR);
  const parts = query.split(PATH_SEPARATOR).filter((part) => part.length > 0);

  if (entered) {
    return { scope: parts, filter: '', entered: true };
  }

  if (parts.length <= 1) {
    return { scope: [], filter: parts[0] ?? '', entered: false };
  }

  return {
    scope: parts.slice(0, -1),
    filter: parts[parts.length - 1],
    entered: false,
  };
};

export const pathStartsWith = (path: string[], scope: string[]): boolean =>
  scope.every(
    (segment, index) => path[index]?.toLowerCase() === segment.toLowerCase(),
  );

const SHORT_QUERY_MAX_LENGTH = 2;

const hasWordPrefix = (value: string, query: string) =>
  value
    .toLowerCase()
    .split(/[\s/_-]+/)
    .some((part) => part.startsWith(query));

export const matchSearchQuery = (
  action: AppSearchAction,
  query: string,
): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const alias = action.alias?.trim().toLowerCase();
  if (alias && (alias === normalized || alias.startsWith(normalized))) {
    return true;
  }

  const prefixFields = [
    action.label,
    action.completion,
    ...(action.path ?? []),
  ].filter((value): value is string => Boolean(value));

  if (prefixFields.some((value) => hasWordPrefix(value, normalized))) {
    return true;
  }

  // Short queries collide with aliases (`ti` ⊆ Notifications, cit).
  // Keep substring matching for longer, more specific terms.
  if (normalized.length <= SHORT_QUERY_MAX_LENGTH) {
    return false;
  }

  return [
    action.label,
    action.description,
    action.completion,
    ...(action.path ?? []),
    ...(action.keywords ?? []),
  ]
    .filter((value): value is string => Boolean(value))
    .some((value) => value.toLowerCase().includes(normalized));
};

export const matchScopedAction = (
  action: AppSearchAction,
  parsed: ParsedSearchQuery,
): boolean => {
  if (!pathStartsWith(action.path, parsed.scope)) {
    return false;
  }

  if (parsed.entered && action.path.length < parsed.scope.length) {
    return false;
  }

  if (!parsed.filter) {
    return true;
  }

  return matchSearchQuery(action, parsed.filter);
};

export const completeSearchQuery = (action: AppSearchAction): string =>
  `${action.completion}${PATH_SEPARATOR}`;

export const popSearchQuery = (query: string): string => {
  const parsed = parseSearchQuery(query);

  if (parsed.entered) {
    return parsed.scope.join(PATH_SEPARATOR);
  }

  if (parsed.scope.length > 0) {
    return parsed.filter
      ? `${parsed.scope.join(PATH_SEPARATOR)}${PATH_SEPARATOR}`
      : parsed.scope.slice(0, -1).join(PATH_SEPARATOR);
  }

  return '';
};